
import { defineComponent, ref, computed, watch, PropType } from 'vue';
import AppDialog from '@/components/AppDialog.vue';

interface PriceListOption {
  priceListId: string;
  priceListName: string;
  priceListTag?: string;
  enginePrice: number;
}

interface SaleLineWithPricelists {
  id: string;
  partNumber: string;
  currentSuggestedPrice: string | null;
  currentPriceListId: string | null;
  multiplePriceLists: PriceListOption[];
}

interface Selection {
  type: 'pricelist' | 'manual';
  priceListId?: string;
  priceListName?: string;
  price?: number;
  manualPrice?: string;
}

export default defineComponent({
  name: 'MultiplePriceListSelector',
  components: { AppDialog },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    saleLines: {
      type: Array as PropType<SaleLineWithPricelists[]>,
      required: true,
    },
    availablePricelists: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },
  emits: ['update:modelValue', 'confirm', 'cancel'],
  setup(props, { emit }) {
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    const selections = ref<Record<string, Selection>>({});
    const initialSelections = ref<Record<string, Selection>>({});

    const saleLinesWithPricelists = computed(() =>
      [...props.saleLines].sort((a, b) => a.partNumber.localeCompare(b.partNumber)),
    );

    // Initialize selections from current prop values
    const initializeSelections = () => {
      // Always reset selections to start fresh
      selections.value = {};
      initialSelections.value = {};

      props.saleLines.forEach((line) => {
        // If there's a suggestedPrice, it takes precedence (manual override)
        if (line.currentSuggestedPrice) {
          const selection: Selection = {
            type: 'manual',
            manualPrice: line.currentSuggestedPrice,
          };
          selections.value[line.id] = { ...selection };
          initialSelections.value[line.id] = { ...selection };
        } else if (line.currentPriceListId) {
          // Only use pricelist selection if there's no manual suggestedPrice
          const pl = line.multiplePriceLists.find((p) => p.priceListId === line.currentPriceListId);
          if (pl) {
            const selection: Selection = {
              type: 'pricelist',
              priceListId: pl.priceListId,
              priceListName: pl.priceListName,
              price: pl.enginePrice,
            };
            selections.value[line.id] = { ...selection };
            initialSelections.value[line.id] = { ...selection };
          }
        }
      });
    };

    // Reset and reinitialize when dialog opens
    watch(
      () => props.modelValue,
      (isOpen) => {
        if (isOpen) {
          initializeSelections();
        }
      },
    );

    // Also initialize on first mount if dialog is already open
    watch(
      () => props.saleLines,
      () => {
        if (props.modelValue) {
          initializeSelections();
        }
      },
      { immediate: true },
    );

    const selectedCount = computed(() => {
      return Object.values(selections.value).filter((s) => {
        if (s.type === 'pricelist') return true;
        if (s.type === 'manual' && s.manualPrice) return true;
        return false;
      }).length;
    });

    // Check if there are any changes compared to initial state
    const hasChanges = computed(() => {
      const currentKeys = Object.keys(selections.value);
      const initialKeys = Object.keys(initialSelections.value);

      // Check if keys are different (added or removed selections)
      if (currentKeys.length !== initialKeys.length) return true;

      // Check if any selection has changed
      for (const key of currentKeys) {
        const current = selections.value[key];
        const initial = initialSelections.value[key];

        if (!initial) return true; // New selection added

        if (current.type !== initial.type) return true;
        if (current.type === 'pricelist' && initial.type === 'pricelist') {
          if (current.priceListId !== initial.priceListId) return true;
        }
        if (current.type === 'manual' && initial.type === 'manual') {
          if (current.manualPrice !== initial.manualPrice) return true;
        }
      }

      return false;
    });

    const formatCurrency = (value: number | string | undefined) => {
      if (value === undefined || value === null || value === '') return '--';
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(numValue)) return '--';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(numValue);
    };

    // Color classes for pricelists: blue, green, purple, yellow, orange, red
    const colorClasses = ['blue', 'green', 'purple', 'yellow', 'orange', 'red'];

    const getColorClass = (index: number): string => {
      return colorClasses[index % colorClasses.length];
    };

    // Get display label for a pricelist (tag if available, otherwise name)
    const getDisplayLabel = (pl: PriceListOption): string => {
      return pl.priceListTag || pl.priceListName;
    };

    const getColorClassByName = (pricelistName: string): string => {
      // Find by matching the display label (tag or name) in availablePricelists
      const index = props.availablePricelists.findIndex((label) => label === pricelistName);
      return index >= 0 ? getColorClass(index) : 'blue';
    };

    // Get color class by pricelist option (uses tag or name)
    const getColorClassByPricelist = (pl: PriceListOption): string => {
      const displayLabel = getDisplayLabel(pl);
      const index = props.availablePricelists.indexOf(displayLabel);
      return index >= 0 ? getColorClass(index) : 'blue';
    };

    const selectPricelist = (lineId: string, pl: PriceListOption) => {
      selections.value[lineId] = {
        type: 'pricelist',
        priceListId: pl.priceListId,
        priceListName: pl.priceListName,
        price: pl.enginePrice,
      };
    };

    const selectAllFromPricelist = (displayLabel: string) => {
      // displayLabel is now the tag or name
      props.saleLines.forEach((line) => {
        const pl = line.multiplePriceLists.find((p) => getDisplayLabel(p) === displayLabel);
        if (pl) {
          selections.value[line.id] = {
            type: 'pricelist',
            priceListId: pl.priceListId,
            priceListName: pl.priceListName,
            price: pl.enginePrice,
          };
        }
        // If the line doesn't have this pricelist, leave the current selection as is
      });
    };

    const confirmSelection = () => {
      const result = Object.entries(selections.value)
        .filter(([, selection]) => {
          if (selection.type === 'pricelist') return true;
          if (selection.type === 'manual' && selection.manualPrice) return true;
          return false;
        })
        .map(([lineId, selection]) => ({
          lineId,
          ...selection,
        }));
      emit('confirm', result);
      dialogVisible.value = false;
    };

    const cancel = () => {
      emit('cancel');
      dialogVisible.value = false;
    };

    return {
      dialogVisible,
      selections,
      saleLinesWithPricelists,
      selectedCount,
      hasChanges,
      formatCurrency,
      getDisplayLabel,
      getColorClass,
      getColorClassByName,
      getColorClassByPricelist,
      selectPricelist,
      selectAllFromPricelist,
      confirmSelection,
      cancel,
    };
  },
});
